/**
 * DanaVoice - Main Application Component
 *
 * Root component with React Router setup and Neon Auth (Stack Auth).
 * Handles route configuration and protected routes.
 */

import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { StackHandler } from '@stackframe/react';
import { stackClientApp } from './stack';
import { AuthProvider } from './contexts/AuthContext';
// Disabled context providers for unused features
// import { FAQProvider } from './contexts/FAQContext';
// import { AppointmentProvider } from './contexts/AppointmentContext';
import { AnalyticsProvider } from './contexts/AnalyticsContext';
// import { MessagesProvider } from './contexts/MessagesContext';
import { ToastProvider } from './contexts/ToastContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { useUser } from '@stackframe/react';

// Layout Components (not lazy loaded - needed immediately)
import { DashboardLayout } from './components/layout/DashboardLayout';
import { IS_HQ_BUILD } from './constants/locations';
import { ElevenLabsWidget } from './components/ElevenLabsWidget';

// Lazy-loaded Page Components for better code splitting
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
// Disabled pages - commented out to reduce bundle size
// const MessagesPage = lazy(() => import('./pages/messages/MessagesPage'));
// const MessageDetailPage = lazy(() => import('./pages/messages/MessageDetailPage'));
// const ResidentsPage = lazy(() => import('./pages/residents/ResidentsPage'));
// const ResidentDetailPage = lazy(() => import('./pages/residents/ResidentDetailPage'));
// const AppointmentsPage = lazy(() => import('./pages/appointments/AppointmentsPage'));
// const FAQPage = lazy(() => import('./pages/FAQPage').then(m => ({ default: m.FAQPage })));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage').then(m => ({ default: m.AnalyticsPage })));
const AgentsOverviewPage = lazy(() => import('./pages/analytics/AgentsOverviewPage'));
const CallLogsPage = lazy(() => import('./pages/call-logs/CallLogsPage'));
const StaffPage = lazy(() => import('./pages/staff/StaffPage'));
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

/**
 * Loading Fallback Component
 *
 * Displayed while lazy-loaded components are being fetched.
 */
function LoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Loading...</p>
      </div>
    </div>
  );
}

/**
 * Protected Route Wrapper
 *
 * Redirects to Stack Auth login if user is not authenticated.
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const user = useUser({ or: 'redirect' });

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-dana-primary"></div>
      </div>
    );
  }

  return <>{children}</>;
}

/**
 * Stack Auth Handler Routes
 *
 * Handles authentication flows (sign-in, sign-up, password reset, etc.)
 */
function HandlerRoutes() {
  const location = useLocation();
  return <StackHandler app={stackClientApp} location={location.pathname} fullPage />;
}

/**
 * App Component
 * 
 * Main application with routing and authentication.
 */
export function App() {
  return (
    <LanguageProvider>
    <AuthProvider>
      <ToastProvider>
        {/* <MessagesProvider>
          <FAQProvider>
            <AppointmentProvider> */}
              <AnalyticsProvider>
                <ElevenLabsWidget />
                <Routes>
            {/* Stack Auth Handler Routes */}
            <Route path="/handler/*" element={<HandlerRoutes />} />

            {/* Protected Routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
            {/* Redirect root to dashboard */}
            <Route index element={<Navigate to="/dashboard" replace />} />

            {/* Dashboard */}
            <Route path="dashboard" element={
              <Suspense fallback={<LoadingFallback />}>
                <DashboardPage />
              </Suspense>
            } />

            {/* Messages - Disabled for now */}
            {/* <Route path="messages" element={
              <Suspense fallback={<LoadingFallback />}>
                <MessagesPage />
              </Suspense>
            } />
            <Route path="messages/:messageId" element={
              <Suspense fallback={<LoadingFallback />}>
                <MessageDetailPage />
              </Suspense>
            } /> */}

            {/* Residents - Disabled for now */}
            {/* <Route path="residents" element={
              <Suspense fallback={<LoadingFallback />}>
                <ResidentsPage />
              </Suspense>
            } />
            <Route path="residents/:residentId" element={
              <Suspense fallback={<LoadingFallback />}>
                <ResidentDetailPage />
              </Suspense>
            } /> */}

            {/* Appointments - Disabled for now */}
            {/* <Route path="appointments" element={
              <Suspense fallback={<LoadingFallback />}>
                <AppointmentsPage />
              </Suspense>
            } /> */}

            {/* FAQ - Disabled for now */}
            {/* <Route path="faq" element={
              <Suspense fallback={<LoadingFallback />}>
                <FAQPage />
              </Suspense>
            } /> */}

            {/* Analytics */}
            <Route path="analytics" element={
              <Suspense fallback={<LoadingFallback />}>
                <AnalyticsPage />
              </Suspense>
            } />

            {/* All-agents overview: Dana HQ build only */}
            {IS_HQ_BUILD && (
              <Route path="analytics/agents" element={
                <Suspense fallback={<LoadingFallback />}>
                  <AgentsOverviewPage />
                </Suspense>
              } />
            )}

            {/* Call Logs */}
            <Route path="call-logs" element={
              <Suspense fallback={<LoadingFallback />}>
                <CallLogsPage />
              </Suspense>
            } />

            {/* Staff Management */}
            <Route path="staff" element={
              <Suspense fallback={<LoadingFallback />}>
                <StaffPage />
              </Suspense>
            } />

            {/* Settings */}
            <Route path="settings" element={
              <Suspense fallback={<LoadingFallback />}>
                <SettingsPage />
              </Suspense>
            } />
            </Route>

            {/* 404 Not Found */}
            <Route path="*" element={
              <Suspense fallback={<LoadingFallback />}>
                <NotFoundPage />
              </Suspense>
            } />
            </Routes>
          </AnalyticsProvider>
        {/* </AppointmentProvider>
      </FAQProvider>
      </MessagesProvider> */}
      </ToastProvider>
    </AuthProvider>
    </LanguageProvider>
  );
}

export default App;